import {
    Block,
    BLOCKS,
    Document,
    helpers,
    Inline,
    MARKS,
    Text
} from '@contentful/rich-text-types';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import slugifyTitle from '../../../contentful-api/slugify-title';

export interface TransformMigratedArticleBodyInput {
    /** The rich text document to transform. */
    readonly document: Document;
}

export interface TransformMigratedArticleBodyOutput {
    /** The transformed rich text document. */
    readonly document: Document;

    /** The table of contents of the document. */
    readonly tableOfContents: readonly TableOfContentsSection[];
}

export interface TableOfContentsSection {
    /** The ID of the section. */
    readonly id: string;

    /** The title of the section. */
    readonly title: string;
}

const HEADER_MAP: ReadonlyMap<BLOCKS, number> = new Map([
    [BLOCKS.HEADING_2, 2],
    [BLOCKS.HEADING_3, 3],
    [BLOCKS.HEADING_4, 4],
    [BLOCKS.HEADING_5, 5],
    [BLOCKS.HEADING_6, 6]
]);

const REVERSE_HEADER_MAP: ReadonlyMap<number, BLOCKS> = new Map(
    [...HEADER_MAP].map(([key, value]) => [value, key])
);

/** The smallest header allowed. */
const SMALLEST_HEADER = 4;

/**
 * Indicates no header was found.
 * Since headers are numbered from 1-6, we use 7 to represent this.
 */
const NO_HEADER = 7;

/**
 * Transforms a migrated KB article as follows:
 * - Proportionately resize header nodes to ensure the biggest header is always {@link BLOCKS.HEADING_2}.
 * - Add an `id` property to the node `data`, so that the header ID can be used during rendering.
 */
export function transformMigratedArticleBody(
    input: TransformMigratedArticleBodyInput
): TransformMigratedArticleBodyOutput {
    const { document } = input;
    const biggestHeader = getBiggestHeader(document);

    // resize headers to ensure the biggest is always `BLOCKS.HEADING_2`
    if (biggestHeader !== NO_HEADER) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const resizeBy = biggestHeader - HEADER_MAP.get(BLOCKS.HEADING_2)!;
        const tableOfContents: TableOfContentsSection[] = [];
        const transformedDocument = updateNode(
            document,
            biggestHeader,
            resizeBy,
            tableOfContents
        );

        return {
            document: transformedDocument,
            tableOfContents
        };
    }

    return { document, tableOfContents: [] };
}

function getBiggestHeader(node: Block, biggest: number = NO_HEADER): number {
    if (node.nodeType === BLOCKS.HEADING_1) {
        /*
         * This should be impossible during publishing, since Contentful validation ensures this is not present.
         * During preview, if the entry is created using the API, it may contain such nodes.
         */
        throw new Error(
            `${BLOCKS.HEADING_1} are not allowed, but the specified rich text document contains one or more.`
        );
    }

    for (const childNode of node.content) {
        if (helpers.isBlock(childNode)) {
            const childValue = getBiggestHeader(childNode, biggest);
            // the lower the value, the bigger the heading
            if (childValue < biggest) {
                biggest = childValue;
            }
        }
    }

    const nodeValue = HEADER_MAP.get(node.nodeType);
    return nodeValue && nodeValue < biggest ? nodeValue : biggest;
}

// eslint-disable-next-line max-params
function updateNode<T extends Block | Inline | Text>(
    node: T,
    biggestHeader: number,
    resizeBy: number,
    tableOfContents: TableOfContentsSection[],
    marks: MARKS[] = []
): T {
    // add marks to text (if applicable)
    if (helpers.isText(node)) {
        return marks.length > 0
            ? {
                  ...node,
                  marks: [...node.marks, ...marks.map((type) => ({ type }))]
              }
            : node;
    }

    const {
        nodeType = null,
        section = null,
        mark = null
    } = helpers.isBlock(node)
        ? processHeaderNode(node, biggestHeader, resizeBy)
        : {};

    // update table of contents
    if (section) {
        tableOfContents.push(section);
    }

    /*
     * Recursively process children nodes and if applicable:
     * - Replace node type (resize header or transform to paragraph)
     * - Add section id to data
     */
    return {
        ...node,
        nodeType: nodeType ?? node.nodeType,
        data: section
            ? {
                  ...node.data,
                  id: section.id
              }
            : node.data,
        content: node.content.map((n) =>
            updateNode(
                n,
                biggestHeader,
                resizeBy,
                tableOfContents,
                mark ? [mark] : []
            )
        )
    };
}

function processHeaderNode(
    node: Block,
    biggestHeader: number,
    resizeBy: number
): {
    nodeType?: BLOCKS;
    section?: TableOfContentsSection;
    mark?: MARKS;
} {
    const headerValue = HEADER_MAP.get(node.nodeType);

    if (!headerValue) {
        // node is not a header, nothing to change
        return {};
    }

    // the lower the value, the bigger the heading
    const resizedHeaderValue = headerValue - resizeBy;

    if (resizedHeaderValue > SMALLEST_HEADER) {
        // headers smaller than allowed are transformed to bold paragraphs
        return {
            nodeType: BLOCKS.PARAGRAPH,
            mark: MARKS.BOLD
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const nodeType = REVERSE_HEADER_MAP.get(resizedHeaderValue)!;

    if (headerValue === biggestHeader) {
        // always resize the biggest header and update table of contents
        const title = documentToPlainTextString(node);
        return {
            nodeType,
            section: {
                id: slugifyTitle(title) ?? title,
                title
            }
        };
    }

    // headers bigger or equal than allowed should be resized
    return { nodeType };
}
